import { useQueryClient } from '@tanstack/react-query';
import { useMemo, type ReactNode } from 'react';
import { useAuth as useOidc } from 'react-oidc-context';

import { AuthContextProvider } from '@/lib/auth/AuthContext';
import { mapOidcUserToConsoleUser } from '@/lib/auth/oidcUserMapper';
import type { ConsoleAuthContext } from '@/lib/auth/types';

interface AuthAdapterProps {
    children: ReactNode;
}

/**
 * Adapts react-oidc-context state to our ConsoleAuthContext shape.
 * This is the only place that knows about react-oidc-context.
 * The rest of the app consumes via useAuth() from `@/lib/auth/AuthContext`.
 *
 * signOut purges the TanStack Query cache before redirecting so a user rotating
 * accounts on the same browser cannot see the previous partner's cached data
 * during the brief window before Keycloak redirects back. Mirrors the pattern
 * EnvSwitcher already applies at Test↔Live switch.
 */
export function AuthAdapter({ children }: AuthAdapterProps) {
    const oidc = useOidc();
    const queryClient = useQueryClient();

    const value = useMemo<ConsoleAuthContext>(() => {
        const user = mapOidcUserToConsoleUser(oidc.user);
        return {
            user,
            isAuthenticated: oidc.isAuthenticated,
            isLoading: oidc.isLoading,
            error: oidc.error ?? null,
            getAccessToken: () => oidc.user?.access_token ?? null,
            signIn: () => {
                void oidc.signinRedirect();
            },
            signOut: () => {
                queryClient.clear();
                void oidc.signoutRedirect();
            },
        };
    }, [oidc, queryClient]);

    return <AuthContextProvider value={value}>{children}</AuthContextProvider>;
}
